// Minimal customer detail at /office/customers/:id. The landing spot for "actioning"
// a risk flag from the Customers list: it shows the customer, their active risk flags
// with the plain-English reason for each, their contact details, and quick links
// onward. Read-only (customers are created when a quote is accepted).
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import type { CreatedPortalUser, PortalAdminUser, RemovePortalUsersPlan, RiskFlag, ServiceHistoryItem, WelcomeDraftStatus } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { customers } from "../../lib/resources";
import type { Customer } from "../../lib/resources";
import { resolvesWelcomePackEligible, welcomePackSettingOf, welcomePackValueOf, type WelcomePackSetting } from "../../lib/welcomePack";
import JobTypeBadge from "../../components/JobTypeBadge";
import { serviceRecordHref } from "../../lib/serviceRecordLink";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { ConfirmButton } from "../../components/ConfirmButton";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { ContactValue } from "../../components/ContactValue";
import { Button } from "../../components/Button";
import { RecordHeader } from "../../components/RecordHeader";
import { AccountSymbol } from "../../components/AccountSymbol";
import { OverflowMenu, overflowItemCls, overflowDangerItemCls } from "../../components/OverflowMenu";
import { Loader } from "../../components/Loader";
import { EmptyState } from "../../components/EmptyState";
import { StatusPill } from "../../components/StatusPill";
import { formatDate, formatDateTime } from "../../lib/format";
import { CommunicationTimeline } from "./CommunicationTimeline";
import { ContactsPanel } from "./ContactsPanel";
import { CustomerCallLog } from "./CustomerCallLog";
import { CustomerOverviewTab } from "./CustomerOverviewTab";
import { CustomerEmailTab } from "./CustomerEmailTab";
import { CustomerRateOverrideSection } from "./CustomerRateOverrideSection";
import { CustomerSlaOverrideSection } from "./CustomerSlaOverrideSection";
import { CustomerLolerArrangementSection } from "./CustomerLolerArrangementSection";
import { CustomerChaseSection } from "./CustomerChaseSection";
import { InsuranceItemsSection } from "./InsuranceItemsSection";
import { EditCustomerContactModal, type CustomerContactFields } from "./EditCustomerContactModal";
import { RepairEstimatesPanel } from "./RepairEstimatesPanel";
import { RaiseWorkMenu } from "./RaiseWorkMenu";
import { XeroContactModal } from "./XeroContactModal";
import { XeroContactLinkModal } from "./XeroContactLinkModal";
import { NoActiveContractConfirm, isNoActiveContractRefusal } from "../../components/NoActiveContractConfirm";

// The six tabs, and what a stray/old ?tab= value falls back to. "details" was this page's default
// tab before this session (Session AQW-follow, Phase 2 C-1/C-2/C-3/C-7/C-17): a bookmarked or
// linked ?tab=details now opens Settings, its renamed successor, rather than 404-ing on a tab that
// no longer exists.
// Session AUB (email system Phase 4, Item 7): "email", the customer's inbound and sent mail, derived live.
const CUSTOMER_TABS = ["overview", "settings", "records", "comms", "calls", "email"] as const;
type CustomerTab = (typeof CUSTOMER_TABS)[number];
const TAB_LABEL: Record<CustomerTab, string> = {
  overview: "Overview",
  settings: "Settings",
  records: "Service records",
  comms: "Communications",
  calls: "Call log",
  email: "Email",
};
function tabFromParam(value: string | null): CustomerTab {
  if (value === "details") return "settings";
  return (CUSTOMER_TABS as readonly string[]).includes(value ?? "") ? (value as CustomerTab) : "overview";
}

const TYPE_LABEL: Record<string, string> = { direct: "Direct", intermediary: "Intermediary (FM)" };
const CHANNEL_LABEL: Record<string, string> = { email: "Email", phone: "Phone", whatsapp: "WhatsApp" };

// Session AOZ: what an ACTIVE welcome email override does, shown under the header while one is set.
// "Follow client type" shows nothing here: its own option label already says what it resolves to,
// and the default page stays uncluttered. Both lines end on the same sentence because the setting
// is deliberately independent of the client type pill beside it, which still decides billing and
// the contract variant, and an office user switching this must not think they changed those too.
const WELCOME_OVERRIDE_NOTE: Record<Exclude<WelcomePackSetting, "follow">, string> = {
  always:
    "Welcome email on: one is drafted in Gmail for you to review and send when a new contract is signed for this customer (never at a renewal), as long as they have a billing email. Their billing and contract terms still follow the client type.",
  never:
    "Welcome email off: none is drafted when a new contract is signed for this customer. Their billing and contract terms still follow the client type.",
};

// One pending welcome email choice: which customer it is for travels with it, so a save still running
// when the page moves to another customer can never be written to the wrong one.
type WelcomeChoice = { customerId: string; setting: WelcomePackSetting };

function field(value: string | null | undefined): string {
  return value && value.trim() ? value : "Not on record";
}

export function CustomerDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || user?.role === "office";
  // The per-customer call-out rate override is admin only (Session ADB).
  const isAdmin = user?.role === "admin";
  const [hasRateOverride, setHasRateOverride] = useState(false);
  // The per-customer call-out response-time override is admin only too (Session ADQ).
  const [hasSlaOverride, setHasSlaOverride] = useState(false);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [flags, setFlags] = useState<RiskFlag[]>([]);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "error">("loading");
  // The active tab lives in the URL (?tab=settings etc; no param = Overview), so a link or a search
  // result can land on a specific tab and Back behaves sensibly (Session AQW-follow, Phase 2).
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = tabFromParam(searchParams.get("tab"));
  function setTab(next: CustomerTab) {
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        if (next === "overview") nextParams.delete("tab");
        else nextParams.set("tab", next);
        return nextParams;
      },
      { replace: false },
    );
  }
  // Bumped by the header "Log a call" button to open the create modal inside the Call log tab.
  const [callOpenSignal, setCallOpenSignal] = useState(0);
  // C-7: billing contact/email/phone edit (D27 open -- a modal, not inline editing, pending that
  // ruling; see EditCustomerContactModal's own header).
  const [showEditContact, setShowEditContact] = useState(false);
  // The admin Xero contact naming check (Session ADV).
  const [showXeroContact, setShowXeroContact] = useState(false);
  // Clear or change the stored Xero contact link (Session BCG).
  const [showXeroLink, setShowXeroLink] = useState(false);
  // Promote a NON_CONTRACT one-off caller to a full customer (Session AFM).
  const [showPromote, setShowPromote] = useState(false);
  const [promoteNotice, setPromoteNotice] = useState<string | null>(null);

  // Delete (admin + office). Only an empty customer can be removed: the server blocks the delete
  // (409 with a plain-English listing) when sites/contracts/quotes/jobs/projects/retained-invoices
  // are attached. The office must type the customer name to confirm. A previously-archived
  // customer shows a Reactivate action instead.
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [archivedNotice, setArchivedNotice] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  async function confirmDelete() {
    if (!customer) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.customers.remove(customer.id);
      navigate("/office/customers");
    } catch (err) {
      // A 409 carries the blocker message (e.g. "still has 2 sites and 1 contract on file").
      setDeleteError(err instanceof ApiError ? err.message || err.code : "Could not delete the customer. Please try again.");
    } finally {
      setDeleting(false);
    }
  }

  async function archive() {
    if (!customer) return;
    setArchiving(true);
    setArchivedNotice(null);
    try {
      await api.customers.archive(customer.id);
      setCustomer((prev) => (prev ? { ...prev, archivedAt: new Date().toISOString() } : prev));
    } catch {
      setArchivedNotice("Could not archive the customer. Please try again.");
    } finally {
      setArchiving(false);
    }
  }

  async function reactivate() {
    if (!customer) return;
    setReactivating(true);
    try {
      await api.customers.reactivate(customer.id);
      setArchivedNotice(null);
      setCustomer((prev) => (prev ? { ...prev, archivedAt: null } : prev));
    } catch {
      setArchivedNotice("Could not reactivate the customer. Please try again.");
    } finally {
      setReactivating(false);
    }
  }

  // Customer NAME inline edit (Session AGI). Until now the name could only be set when the record
  // was created, or through the AFM promote flow, which is reachable only for a NON_CONTRACT record.
  // So a company whose legal entity name was typed wrong, or which has since changed its name, could
  // not be corrected anywhere in the office at all. PATCH /api/customers/:id has always accepted a
  // name (bodySchema.partial()); this is the missing control, and it applies to every customer.
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  function startEditName() {
    if (!customer) return;
    setNameDraft(customer.name);
    setNameError(null);
    setEditingName(true);
  }

  async function saveName() {
    if (!customer) return;
    const next = nameDraft.trim();
    // Mirrors the server rule (bodySchema: trimmed, 1 to 200) so the office gets the message here
    // rather than a bare failed request.
    if (!next) {
      setNameError("Enter the customer name.");
      return;
    }
    if (next.length > 200) {
      setNameError("That name is too long. Keep it to 200 characters.");
      return;
    }
    if (next === customer.name) {
      setEditingName(false);
      return;
    }
    setNameSaving(true);
    setNameError(null);
    try {
      const updated = await customers.update(customer.id, { name: next });
      // PATCH returns the customer without _count; keep the count we already have.
      setCustomer((prev) => (prev ? { ...prev, ...updated, _count: prev._count } : updated));
      setEditingName(false);
    } catch {
      setNameError("Could not save the name. Please try again.");
    } finally {
      setNameSaving(false);
    }
  }

  // Secondary-contact inline edit. The secondary email is CC'd on customer mail.
  const [editingSec, setEditingSec] = useState(false);
  const [secForm, setSecForm] = useState({ name: "", role: "", email: "", phone: "" });
  const [secSaving, setSecSaving] = useState(false);
  const [secError, setSecError] = useState<string | null>(null);

  // Communication preferences inline edit (informational: how to reach this customer).
  const [editingPrefs, setEditingPrefs] = useState(false);
  const [prefsForm, setPrefsForm] = useState<{ preferredChannel: "email" | "phone" | "whatsapp"; whatsappNumber: string; marketingOptIn: boolean }>({
    preferredChannel: "email",
    whatsappNumber: "",
    marketingOptIn: false,
  });
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsError, setPrefsError] = useState<string | null>(null);

  function startEditPrefs() {
    if (!customer) return;
    setPrefsForm({
      preferredChannel: customer.preferredChannel ?? "email",
      whatsappNumber: customer.whatsappNumber ?? "",
      marketingOptIn: customer.marketingOptIn ?? false,
    });
    setPrefsError(null);
    setEditingPrefs(true);
  }

  async function savePrefs() {
    if (!customer) return;
    setPrefsSaving(true);
    setPrefsError(null);
    try {
      const updated = await customers.update(customer.id, {
        preferredChannel: prefsForm.preferredChannel,
        whatsappNumber: prefsForm.whatsappNumber,
        marketingOptIn: prefsForm.marketingOptIn,
      });
      setCustomer((prev) => (prev ? { ...prev, ...updated, _count: prev._count } : updated));
      setEditingPrefs(false);
    } catch {
      setPrefsError("Could not save the preferences. Please try again.");
    } finally {
      setPrefsSaving(false);
    }
  }

  // Welcome email setting (Session AOZ). Saves the moment it changes, with no confirm step: it
  // changes a setting, drafts or sends nothing, and is reversible from the same control. "Follow client
  // type" must write an EXPLICIT null, never omit the key and never write false: null is the reset path
  // back to "no override" (RULE 1 and RULE 6), while an omitted key would leave the override in place
  // and false would be "Never draft". The select is controlled by the STORED value; the one exception
  // is the choice in flight, held in welcomePending so the select shows what was just picked instead of
  // snapping back to the old option for the length of the request. Clearing it (on success or failure)
  // hands the select back to what is really on the record.
  //
  // THE SELECT IS NEVER DISABLED, deliberately (Session AOZ review). A keyboard user arrowing through a
  // closed select fires a change on every step, and disabling it while the first save runs drops their
  // focus mid-choice. Instead the saves are SERIALISED: one request at a time, and whatever was picked
  // last while one was running is saved as soon as it returns. So the record always ends on the final
  // choice, and two responses can never land out of order. Each choice carries its own customer id, so
  // a save still running when the page moves to another customer can never write to the wrong one.
  const [welcomePending, setWelcomePending] = useState<WelcomePackSetting | null>(null);
  const [welcomeError, setWelcomeError] = useState<string | null>(null);
  const welcomeWanted = useRef<WelcomeChoice | null>(null);
  const welcomeSaving = useRef(false);

  async function saveWelcomePack(next: WelcomePackSetting) {
    if (!customer) return;
    welcomeWanted.current = { customerId: customer.id, setting: next };
    setWelcomePending(next);
    setWelcomeError(null);
    if (welcomeSaving.current) return; // the save already running picks this choice up when it returns
    welcomeSaving.current = true;
    try {
      // Typed explicitly: the assignment just above narrows welcomeWanted.current to non-null, and the
      // loop has to be able to read it back as null once nothing newer is waiting.
      let wanted: WelcomeChoice | null = welcomeWanted.current;
      while (wanted) {
        const saved: WelcomeChoice = wanted;
        const updated = await customers.update(saved.customerId, { receivesWelcomePack: welcomePackValueOf(saved.setting) });
        // PATCH returns the customer without _count; keep the count we already have, and never merge a
        // save into a different customer than the one it was made for.
        setCustomer((prev) => (prev && prev.id === saved.customerId ? { ...prev, ...updated, _count: prev._count } : prev));
        if (welcomeWanted.current === saved) welcomeWanted.current = null; // nothing newer was picked meanwhile
        wanted = welcomeWanted.current;
      }
    } catch {
      welcomeWanted.current = null;
      setWelcomeError("Could not save the welcome email setting. Please try again.");
    } finally {
      welcomeSaving.current = false;
      setWelcomePending(null);
    }
  }

  function startEditSecondary() {
    if (!customer) return;
    setSecForm({
      name: customer.secondaryContactName ?? "",
      role: customer.secondaryContactRole ?? "",
      email: customer.secondaryContactEmail ?? "",
      phone: customer.secondaryContactPhone ?? "",
    });
    setSecError(null);
    setEditingSec(true);
  }

  async function saveSecondary() {
    if (!customer) return;
    setSecSaving(true);
    setSecError(null);
    try {
      const updated = await customers.update(customer.id, {
        secondaryContactName: secForm.name,
        secondaryContactRole: secForm.role,
        secondaryContactEmail: secForm.email,
        secondaryContactPhone: secForm.phone,
      });
      // PATCH returns the customer without _count; keep the count we already have.
      setCustomer((prev) => (prev ? { ...prev, ...updated, _count: prev._count } : updated));
      setEditingSec(false);
    } catch {
      setSecError("Could not save. Please check the email is valid and try again.");
    } finally {
      setSecSaving(false);
    }
  }

  // C-7: billing contact/email/phone. The modal owns its own field state; this page owns the API
  // call, exactly as every other inline edit on it already does (saveName, saveSecondary,
  // savePrefs). Returns whether it landed, so the modal knows whether to close itself.
  const [contactError, setContactError] = useState<string | null>(null);
  async function saveContact(body: CustomerContactFields): Promise<boolean> {
    if (!customer) return false;
    setContactError(null);
    try {
      const updated = await customers.update(customer.id, { billingContact: body.billingContact, billingEmail: body.billingEmail, phone: body.phone });
      setCustomer((prev) => (prev ? { ...prev, ...updated, _count: prev._count } : updated));
      return true;
    } catch {
      setContactError("Could not save. Please check the email is valid and try again.");
      return false;
    }
  }

  // Manual NPS entry (fallback for customers who reply by phone).
  const [npsOpen, setNpsOpen] = useState(false);
  const [npsInput, setNpsInput] = useState("");
  const [npsSaving, setNpsSaving] = useState(false);
  const [npsError, setNpsError] = useState<string | null>(null);

  async function recordNps() {
    if (!customer) return;
    const score = Number(npsInput);
    if (npsInput.trim() === "" || !Number.isInteger(score) || score < 0 || score > 10) {
      setNpsError("Enter a whole number from 0 to 10.");
      return;
    }
    setNpsSaving(true);
    setNpsError(null);
    try {
      await api.customers.recordNps(customer.id, score);
      setCustomer((prev) => (prev ? { ...prev, latestNpsScore: score } : prev));
      setNpsOpen(false);
      setNpsInput("");
      // A low score may have added the LOW_NPS flag -- refresh the flags quietly.
      api.customers.risk(customer.id).then((r) => setFlags(r.flags)).catch(() => undefined);
    } catch {
      setNpsError("Could not record the score. Please try again.");
    } finally {
      setNpsSaving(false);
    }
  }

  useEffect(() => {
    let live = true;
    setState("loading");
    // Fetch the one customer (works for an archived one too, unlike the default list).
    api.customers
      .get(id)
      .then((cust) => {
        if (!live) return;
        setCustomer(cust);
        setState("ready");
        // Risk is supplementary: if it fails, still show the customer with no flags.
        api.customers.risk(id).then((r) => live && setFlags(r.flags)).catch(() => undefined);
      })
      .catch((err) => {
        if (!live) return;
        setState(err instanceof ApiError && err.status === 404 ? "notfound" : "error");
      });
    return () => {
      live = false;
    };
  }, [id]);

  // "Draft welcome email now" (Session APC). The welcome is drafted ONCE, at contract signing, so a customer
  // whose contract is already active and who was never drafted one (Austin Court signed hours before the
  // welcome setting existed) had no way to get it. This offers it, for the customer's current ACTIVE contract.
  //
  // WHETHER IT IS OFFERED at all follows the preview button's rule, the one welcome eligibility decision
  // (resolvesWelcomePackEligible), never re-derived here; never for a one-off NON_CONTRACT caller (there is
  // no contract to welcome), and never for an archived customer (their portal is closed, so the welcome would
  // promise a login that does not work; the server refuses it too). WHAT it can do is read from the server
  // (the page holds no contract data of its own), and that read is the SAME check the press enforces.
  //
  // The status is re-read whenever eligibility flips, so an intermediary switched to Always draft gains the
  // button the moment the setting saves, as the preview button does. A failed read leaves the action simply
  // not offered: it is an extra, and must never break the page around it.
  const welcomeDraftOffered =
    customer != null && customer.customerType !== "NON_CONTRACT" && !customer.archivedAt && resolvesWelcomePackEligible(customer);
  const welcomeDraftCustomerId = customer?.id ?? null;
  const [welcomeDraft, setWelcomeDraft] = useState<WelcomeDraftStatus | null>(null);
  const [welcomeDrafting, setWelcomeDrafting] = useState(false);
  const [welcomeDraftError, setWelcomeDraftError] = useState<string | null>(null);
  const [welcomeDraftNotice, setWelcomeDraftNotice] = useState<string | null>(null);

  useEffect(() => {
    setWelcomeDraft(null);
    setWelcomeDraftError(null);
    setWelcomeDraftNotice(null);
    // A press still running for the PREVIOUS customer must not leave this one's button busy (its answer is
    // discarded by draftWelcome's stillHere guard when it lands).
    setWelcomeDrafting(false);
    if (!welcomeDraftCustomerId || !welcomeDraftOffered) return;
    let live = true;
    api.customers
      .welcomeEmailDraftStatus(welcomeDraftCustomerId)
      .then((s) => {
        if (live) setWelcomeDraft(s);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [welcomeDraftCustomerId, welcomeDraftOffered]);

  // The customer this page is showing RIGHT NOW. The customer route has no key, so React keeps this component
  // (and any press still in flight) alive when the URL moves to another customer, and the answer to customer A's
  // press must never be written onto customer B. The setting save above carries its customer id for the same
  // reason; this is the same guard for a press.
  const shownCustomerId = useRef<string | null>(null);
  shownCustomerId.current = customer?.id ?? null;

  async function draftWelcome() {
    if (!customer) return;
    const customerId = customer.id;
    const stillHere = () => shownCustomerId.current === customerId;
    setWelcomeDrafting(true);
    setWelcomeDraftError(null);
    setWelcomeDraftNotice(null);
    try {
      const done = await api.customers.draftWelcomeEmail(customerId);
      if (!stillHere()) return;
      // Flip to the drafted state from what the server just said, rather than re-reading: it is the stamp the
      // press itself wrote. "Drafted", never "sent": nothing has reached the customer, the office does that.
      setWelcomeDraft((prev) => (prev ? { ...prev, draftedAt: done.draftedAt, canDraft: false } : prev));
      // What actually travelled with it. Every attachment is best-effort, so a rendering fault can leave the
      // draft with none, and the office reviewing it should be told rather than find out on the customer's side.
      const attached = done.documents.length
        ? `Attached: ${done.documents.join(", ")}.`
        : "No documents could be attached, so add them by hand before you send it.";
      setWelcomeDraftNotice(
        `Welcome email drafted for ${done.siteName}. ${attached} Open Gmail Drafts to review it and send it to ${done.to}. Nothing has been sent.`,
      );
    } catch (err) {
      if (!stillHere()) return;
      const refused = err instanceof ApiError;
      setWelcomeDraftError(refused ? err.message || err.code : "Could not draft the welcome email. Please try again.");
      // A refusal can mean the record moved under the page (drafted from another tab, archived, a billing email
      // removed), so re-read what is really on offer instead of leaving a button that will refuse again.
      api.customers
        .welcomeEmailDraftStatus(customerId)
        .then((s) => {
          if (!stillHere()) return;
          setWelcomeDraft(s);
          // A LOST RESPONSE (a network error, not the server refusing) can mean the press worked: the request
          // reached the platform and only the answer went missing. If the re-read now says drafted, "could not
          // draft, try again" would contradict a disabled "drafted" button beside it, so say what is known.
          if (!refused && s.draftedAt) {
            setWelcomeDraftError(
              "The request did not finish, but this contract now shows as drafted. Check Gmail Drafts before doing anything else.",
            );
          }
        })
        .catch(() => undefined);
    } finally {
      // Only the customer still on screen owns the busy flag; the effect above already cleared it on a move.
      if (stillHere()) setWelcomeDrafting(false);
    }
  }

  const many = flags.length >= 2;

  // The welcome email note shows only while an override is active, and never for a NON_CONTRACT
  // caller, whose control is hidden (a note describing a control that is not there would mislead).
  const welcomeSetting: WelcomePackSetting = welcomePackSettingOf(customer?.receivesWelcomePack);
  const welcomeNote =
    customer && customer.customerType !== "NON_CONTRACT" && welcomeSetting !== "follow"
      ? WELCOME_OVERRIDE_NOTE[welcomeSetting]
      : null;

  return (
    <div>
      {!customer && (
        <Link to="/office/customers" className="font-menu text-xs tracking-wide text-ele-green hover:underline">
          Back to customers
        </Link>
      )}

      {state === "loading" ? (
        <Loader shape="panel" className="mt-4" />
      ) : state === "notfound" ? (
        <p className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          That customer could not be found.
        </p>
      ) : state === "error" ? (
        <p role="alert" className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load this customer. Please try again in a moment.
        </p>
      ) : customer ? (
        <>
          {/* Design system Session 5: the shared record header (spec 3.1). The editable name is the
              header's title (the input while editing); the pencil, the Save/Cancel pair, the type and
              status pills and the welcome email setting sit beside it as the header's status slot,
              and the action buttons are its actions. */}
          <RecordHeader
            className="mb-4 mt-2"
            backTo="/office/customers"
            backLabel="Back to customers"
            title={
              editingName ? (
                <input
                  aria-label="Customer name"
                  value={nameDraft}
                  autoFocus
                  maxLength={200}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void saveName();
                    if (e.key === "Escape") setEditingName(false);
                  }}
                  className="min-h-11 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 font-heading text-2xl tracking-wide text-ele-strong outline-none focus:border-ele-green sm:w-[28rem]"
                />
              ) : (
                customer.name
              )
            }
            status={
              <>
                {/* Session SC9B: the account symbol beside the name (not inside the heading, so its name stays the customer's), derived live from Xero. A flag, never a block. */}
                {!editingName && <AccountSymbol customerId={customer.id} className="!ml-0" />}
                {editingName ? (
                  <>
                    <Button size="sm" onClick={() => void saveName()} disabled={nameSaving}>
                      {nameSaving ? "Saving..." : "Save"}
                    </Button>
                    <button
                      onClick={() => setEditingName(false)}
                      disabled={nameSaving}
                      className="max-sm:min-h-11 rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:bg-ele-inset disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={startEditName}
                    title="Edit the customer name"
                    aria-label="Edit the customer name"
                    className="flex min-h-11 items-center rounded-ele px-2 py-1 text-ele-body transition hover:bg-ele-inset hover:text-ele-purple max-sm:min-h-11"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
                    </svg>
                  </button>
                )}
                {/* Session AGZ: the chips are StatusPills, matching the pills beside them. Non-contract
                    stays AMBER: the locked AFN/AGC billing-basis colour. */}
                <StatusPill word={TYPE_LABEL[customer.clientType ?? "direct"] ?? "Direct"} tone="grey" />
                {/* Session AOZ: the welcome email setting, right beside the client type pill because
                    that pill used to decide it too. The client type still decides billing and the
                    contract variant; whether this customer is drafted the welcome email at contract
                    signing is now its own setting. "Follow client type" stores null and its label
                    says what the client type gives THIS customer, because an override must say what
                    it is masking (RULE 6). The decision itself is resolvesWelcomePackEligible
                    (src/lib/welcomePack.ts), never re-derived on this page. Hidden for a NON_CONTRACT
                    one-off caller: that record never gets a contract, so the setting would silently
                    do nothing (the preview button and the rate override are hidden for one on the
                    same reasoning). */}
                {customer.customerType !== "NON_CONTRACT" && (
                  <div className="flex flex-wrap items-center gap-2">
                    <label htmlFor="welcome-email-setting" className="font-menu text-xs tracking-wide text-ele-body">
                      Welcome email
                    </label>
                    <select
                      id="welcome-email-setting"
                      value={welcomePending ?? welcomeSetting}
                      onChange={(e) => void saveWelcomePack(e.target.value as WelcomePackSetting)}
                      aria-busy={welcomePending !== null}
                      aria-describedby={welcomeNote ? "welcome-email-setting-note" : undefined}
                      className="max-sm:min-h-11 rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1 font-menu text-xs tracking-wide text-ele-strong outline-none focus:border-ele-green disabled:opacity-50"
                    >
                      <option value="follow">
                        {resolvesWelcomePackEligible({ clientType: customer.clientType, receivesWelcomePack: null })
                          ? "Follow client type (drafted)"
                          : "Follow client type (not drafted)"}
                      </option>
                      <option value="always">Always draft</option>
                      <option value="never">Never draft</option>
                    </select>
                  </div>
                )}
                {customer.customerType === "NON_CONTRACT" && <StatusPill word="Non-contract" tone="amber" />}
                {customer.archivedAt && <StatusPill word="Archived" tone="grey" />}
                {isAdmin && hasRateOverride && <StatusPill word="Rate override" tone="green" />}
                {isAdmin && hasSlaOverride && <StatusPill word="Response times" tone="green" />}
              </>
            }
            actions={
              <>
                {/* SESSION SC7B (the calm screens, part 2): the header keeps the two things a person does here every day
                    (Log a call, Raise work) and, for the two records whose main job is different, that main job
                    (Promote to full customer on a one-off caller; Reactivate on an archived customer). Every other action is in the
                    shared overflow menu at the end of the row, one press further. Nothing was removed and every flow, confirm
                    and modal is unchanged. */}
                <Button size="sm" onClick={() => { setTab("calls"); setCallOpenSignal((n) => n + 1); }}>
                  Log a call
                </Button>
                {/* Session ASM (W-12): raise work for THIS customer through the ordinary flows, pre-filled with the
                    customer (the flows then narrow the site and lift pickers to theirs). Not on an archived customer. */}
                {!customer.archivedAt && canDelete && (
                  <RaiseWorkMenu
                    options={["callout", "repair", "safed", "quote"]}
                    context={{ customerId: customer.id, customerName: customer.name, customerEmail: customer.billingEmail ?? null }}
                  />
                )}
                {/* Session AFM: promote a one-off caller to a full customer. Prominent (the blue
                    #2CB9F3 the prompt prescribes) because it is this page's main action for a
                    NON_CONTRACT record. */}
                {customer.customerType === "NON_CONTRACT" && (
                  <button
                    onClick={() => setShowPromote(true)}
                    className="max-sm:min-h-11 rounded-ele border border-ele-loler px-3 py-1 font-menu text-xs tracking-wide text-ele-loler transition hover:bg-ele-inset"
                  >
                    Promote to full customer
                  </button>
                )}
                {canDelete && customer.archivedAt && (
                  <Button variant="secondary" size="sm" onClick={reactivate} disabled={reactivating}>
                    {reactivating ? "Reactivating..." : "Reactivate"}
                  </Button>
                )}
                <OverflowMenu label="More customer actions" closeOnSelect>
                  {/* Session AHI: read-only preview of the welcome email this customer would be drafted
                      on contract activation (built AHH, extended for this preview). Offered exactly
                      when that email would be drafted, as decided by the one welcome eligibility rule,
                      resolvesWelcomePackEligible (src/lib/welcomePack.ts, the office mirror of the
                      server decision the preview route and the activation draft both use). Until
                      Session AOZ this was "direct clients only"; it now follows the Welcome email
                      setting in the title row, which in turn follows the client type unless the office
                      has overridden it. So an intermediary switched to Always draft gains this item,
                      and a direct client switched to Never draft loses it, the moment the setting saves.
                      Separately hidden for a NON_CONTRACT one-off caller (review fix): that record
                      never acquires a quote or contract by design, so there is never anything to
                      preview for it, matching the Welcome email setting and the rate override, both
                      hidden for one on the same reasoning. Opens in a new tab; no email is sent and
                      nothing is written (the code shown is generated fresh and never persisted); a
                      customer with no contract yet, or that could not be found, opens a plain-English
                      page rather than raw JSON. */}
                  {resolvesWelcomePackEligible(customer) && customer.customerType !== "NON_CONTRACT" && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => window.open(api.customers.welcomeEmailPreviewHref(customer.id), "_blank", "noopener,noreferrer")}
                      className={overflowItemCls}
                    >
                      Preview welcome email
                    </button>
                  )}
                  {/* Session APC: the WRITE the preview beside it is not. The welcome is drafted once, at contract
                      signing, so a customer whose contract is already active and who was never drafted one (or
                      whose setting was switched to Always draft afterwards) had no way to get it. Offered only
                      when the server says it can be done for the customer's current ACTIVE contract; once it has
                      been, it stays as a disabled "drafted" state rather than vanishing, so the office can see it
                      was done and when. A two-step press that NAMES the recipient (the branded-confirm rule for a
                      consequential action): it also creates the customer's portal login if they have none. It
                      drafts into Gmail for the office to review and send, and never sends. */}
                  {welcomeDraftOffered && welcomeDraft?.contract && welcomeDraft.draftedAt && (
                    <button type="button" role="menuitem" disabled className={`${overflowItemCls} cursor-not-allowed opacity-70`}>
                      Welcome email drafted {formatDate(welcomeDraft.draftedAt)}
                    </button>
                  )}
                  {welcomeDraftOffered && welcomeDraft?.contract && !welcomeDraft.draftedAt && welcomeDraft.canDraft && (
                    <ConfirmButton
                      onConfirm={() => void draftWelcome()}
                      label="Draft welcome email now"
                      confirmLabel={`Press again to draft it for ${welcomeDraft.to ?? customer.billingEmail ?? "the billing email"} (${welcomeDraft.contract.siteName})`}
                      title="Drafts the welcome email into Gmail Drafts for you to review. Creates the customer's portal login if they have none. Nothing is sent."
                      busy={welcomeDrafting}
                      busyLabel="Drafting..."
                      className={overflowItemCls}
                    />
                  )}
                  {/* Session ADV: check (and, if needed, correct) how this customer is named in Xero.
                      Admin only, because pressing through it changes live accounting data. */}
                  {isAdmin && (
                    <button type="button" role="menuitem" onClick={() => setShowXeroContact(true)} className={overflowItemCls}>
                      Xero contact
                    </button>
                  )}
                  {/* Session BCG: clear or change which Xero contact this customer is linked to (the way to act on
                      merge_customers' "clear the stale link"). Office and admin; it never writes to Xero. */}
                  <button type="button" role="menuitem" onClick={() => setShowXeroLink(true)} className={overflowItemCls}>
                    Change Xero contact
                  </button>
                  {canDelete && !customer.archivedAt && (
                    <>
                      {/* Archive is the reversible option, and for most customers it is the ONLY one
                          that will work: the delete beside it is refused for anything with a site,
                          quote, job or invoice attached. Two-step, because it takes the customer off
                          the register the office works from. */}
                      <ConfirmButton
                        onConfirm={archive}
                        label="Archive"
                        confirmLabel="Press again to archive"
                        busy={archiving}
                        busyLabel="Archiving..."
                        className={overflowItemCls}
                      />
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => { setDeleteError(null); setShowDelete(true); }}
                        className={overflowDangerItemCls}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </OverflowMenu>
              </>
            }
          />
          {/* Session AOZ: a plain wrapper so the welcome email note and its error sit on their own
              line UNDER the header row. A long note would squeeze the action buttons if it shared
              their row; here it is capped (max-w-prose) and wraps. With nothing showing the wrapper is
              empty and takes no space. */}
          {(editingName || welcomeNote || welcomeError || welcomeDraftNotice || welcomeDraftError || (welcomeDraftOffered && welcomeDraft?.contract && !welcomeDraft.draftedAt && !welcomeDraft.canDraft && welcomeDraft.refusal)) && (
            <div className="-mt-2 mb-4">
              {editingName && (
                <>
                  {nameError && (
                    <p role="alert" className="text-xs text-ele-danger">
                      {nameError}
                    </p>
                  )}
                  {/* Renaming here does NOT rename a linked Xero contact: that is the deliberate
                      Session ADV action, and it remains the only way a name travels V2 -> Xero.
                      AGI's wording ALSO warned that the daily sync could write the Xero name back
                      over a correction. Session AGJ removed that write (Director decision), so the
                      warning is now FALSE and had to go: leaving it would make the office distrust
                      a correction that is in fact permanent. The remaining sentence is true whether
                      or not this customer has a Xero contact, so it needs no new payload field. */}
                  <p className="text-xs text-ele-body">
                    This changes the name in the platform only, and it sticks. It does not rename the
                    contact in Xero. To change Xero too, use the Xero contact action.
                  </p>
                </>
              )}
              {welcomeNote && (
                <p id="welcome-email-setting-note" className="mt-2 max-w-prose text-xs text-ele-body">
                  {welcomeNote}
                </p>
              )}
              {welcomeError && (
                <p role="alert" className="mt-2 text-xs text-ele-danger">
                  {welcomeError}
                </p>
              )}
              {/* Session APC: why "Draft welcome email now" is not on offer for a contract that could be
                  welcomed, as VISIBLE text. A disabled control's title never paints, so the reason is
                  never left there. The sentence is the server's own, from the same check the press runs. */}
              {welcomeDraftOffered && welcomeDraft?.contract && !welcomeDraft.draftedAt && !welcomeDraft.canDraft && welcomeDraft.refusal && (
                <p className="mt-2 max-w-prose text-xs text-ele-body">
                  {welcomeDraft.refusal.message}
                </p>
              )}
              {welcomeDraftNotice && (
                <p role="status" className="mt-2 max-w-prose text-xs text-ele-strong">
                  {welcomeDraftNotice}
                </p>
              )}
              {welcomeDraftError && (
                <p role="alert" className="mt-2 max-w-prose text-xs text-ele-danger">
                  {welcomeDraftError}
                </p>
              )}
            </div>
          )}

          {archivedNotice && (
            <p role="status" className="mb-4 rounded-ele border border-amber-500 bg-amber-500/10 px-4 py-3 text-sm text-amber-300">
              {archivedNotice}
            </p>
          )}

          {/* Session ACG locked tab treatment (was pill-style buttons): quiet text tabs,
              full-purple underline on the active tab only. Session AQW-follow (Phase 2): the
              active tab lives in the URL, not local state -- see tabFromParam/setTab above. */}
          <div className="ele-tabs mb-5">
            {CUSTOMER_TABS.map((t) => (
              <button key={t} onClick={() => setTab(t)} className={`ele-tab ${tab === t ? "is-active" : ""}`}>
                {TAB_LABEL[t]}
              </button>
            ))}
          </div>

          {tab === "records" ? (
            <CustomerServiceRecords customerId={customer.id} />
          ) : tab === "comms" ? (
            <CommunicationTimeline target={{ contactType: "customer", contactId: customer.id }} />
          ) : tab === "calls" ? (
            <CustomerCallLog customerId={customer.id} customerName={customer.name} openSignal={callOpenSignal} />
          ) : tab === "email" ? (
            <CustomerEmailTab customerId={customer.id} />
          ) : tab === "settings" ? (
          <div className="space-y-5">
            {/* Portal access (Session AFH): this customer's Client Portal logins, with a one-click
                Send invite / Send portal link (office/admin since Session AFI). Moved here from the
                old default tab, Session AQW-follow (Phase 2): office/admin settings that are not
                part of the daily "why did I open this customer" view. */}
            <PortalAccessCard customerId={customer.id} customerName={customer.name} archived={Boolean(customer.archivedAt)} isAdmin={isAdmin} />

            {/* Communication preferences -- informational: how this customer prefers contact. */}
            <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h2 className="font-heading text-lg text-ele-blue">Communication preferences</h2>
                {!editingPrefs && (
                  <button
                    onClick={startEditPrefs}
                    className="rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
                  >
                    Edit
                  </button>
                )}
              </div>
              <p className="mb-3 text-xs text-ele-body">
                How this customer prefers to be contacted. For information only at this stage: it is not
                yet used to route or suppress automated messages.
              </p>
              {editingPrefs ? (
                <div className="space-y-3">
                  <label className="block">
                    <span className="mb-1 block font-menu text-xs tracking-wide text-ele-body">Preferred channel</span>
                    <select
                      value={prefsForm.preferredChannel}
                      onChange={(e) => setPrefsForm((f) => ({ ...f, preferredChannel: e.target.value as "email" | "phone" | "whatsapp" }))}
                      className="w-full rounded-ele border border-ele-purple-accent bg-ele-field-inset px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green"
                    >
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="whatsapp">WhatsApp</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block font-menu text-xs tracking-wide text-ele-body">WhatsApp number (for inbound contact)</span>
                    <input
                      type="text"
                      value={prefsForm.whatsappNumber}
                      onChange={(e) => setPrefsForm((f) => ({ ...f, whatsappNumber: e.target.value }))}
                      className="w-full rounded-ele border border-ele-purple-accent bg-ele-field-inset px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green"
                    />
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={prefsForm.marketingOptIn}
                      onChange={(e) => setPrefsForm((f) => ({ ...f, marketingOptIn: e.target.checked }))}
                    />
                    <span className="text-sm text-ele-strong">Opted in to marketing</span>
                  </label>
                  {prefsError && <p role="alert" className="text-sm text-ele-danger">{prefsError}</p>}
                  <div className="flex gap-2">
                    <Button variant="secondary" size="sm" onClick={savePrefs} disabled={prefsSaving}>
                      {prefsSaving ? "Saving..." : "Save"}
                    </Button>
                    <button
                      onClick={() => setEditingPrefs(false)}
                      disabled={prefsSaving}
                      className="rounded-ele border border-ele-purple-accent px-4 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <dl className="space-y-1 text-sm">
                  <div className="flex justify-between gap-3"><dt className="text-ele-body">Preferred channel</dt><dd className="min-w-0 break-words text-right text-ele-strong">{CHANNEL_LABEL[customer.preferredChannel ?? "email"]}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-ele-body">WhatsApp number</dt><dd className="min-w-0 break-words text-right text-ele-strong">{field(customer.whatsappNumber)}</dd></div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-ele-body">Marketing opt-in</dt>
                    <dd className="min-w-0 break-words text-right text-ele-strong">
                      {customer.marketingOptIn
                        ? `Yes${customer.marketingOptInAt ? ` (since ${formatDate(customer.marketingOptInAt)})` : ""}`
                        : "No"}
                    </dd>
                  </div>
                </dl>
              )}
            </section>

            {/* Per-customer call-out rate override (Session ADB) -- admin only, and never for a
                one-off caller: a negotiated rate is an in-contract concept, so every pricing path
                refuses it on that basis and the server 409s the save. Offering the form would be
                offering a setting that silently does nothing. */}
            {isAdmin && customer.customerType !== "NON_CONTRACT" && (
              <CustomerRateOverrideSection customerId={customer.id} onActiveChange={setHasRateOverride} />
            )}

            {/* Per-customer call-out response times (Session ADQ) -- admin only, same gate. */}
            {isAdmin && <CustomerSlaOverrideSection customerId={customer.id} onActiveChange={setHasSlaOverride} />}

            {/* Who arranges the customer's LOLER examinations, and the re-bill (Session AUF, DL-1 to DL-3). Office and admin. */}
            <CustomerLolerArrangementSection customerId={customer.id} />
            <InsuranceItemsSection customerId={customer.id} />

            {/* Payment terms and the two chase flags (Session AUH, Phase 7 item 3b). Office and admin. */}
            <CustomerChaseSection customer={customer} onSaved={(updated) => setCustomer((prev) => (prev ? { ...prev, ...updated, _count: prev._count } : updated))} />
          </div>
          ) : (
          <div className="space-y-5">
          <div className="grid gap-5 lg:grid-cols-2">
            {/* Risk flags -- the reason this page is the place to act. */}
            <section
              className={`rounded-ele border bg-ele-surface p-5 ${
                flags.length === 0 ? "border-ele-purple-accent" : many ? "border-ele-danger" : "border-amber-500"
              }`}
            >
              <h2 className="mb-3 font-heading text-lg text-ele-blue">Risk flags</h2>
              {flags.length === 0 ? (
                <p className="text-sm text-ele-green">No active risk flags.</p>
              ) : (
                // Flat rows with amber pills (Session ACG: the coloured left-accent stripe
                // pattern is banned app-wide; colour lives in the pill, not the chrome).
                <ul className="space-y-3">
                  {flags.map((f) => (
                    <li key={f.key}>
                      <StatusPill word={f.label} tone="amber" />
                      <p className="mt-1 text-sm text-ele-body">{f.summary}</p>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Contacts (Session AQW-follow, Phase 2 C-7): billing contact/email/phone (now with a
                working Edit control -- previously a bare read-only <dl>) merged with the secondary
                contact, notes and latest NPS, which used to be their own separate cards. */}
            <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
              <div className="mb-3 flex items-center justify-between gap-3">
                <h2 className="font-heading text-lg text-ele-blue">Contacts</h2>
                {/* "Edit contact details", not the bare "Edit" the Secondary contact card below
                    uses, because both are visible on this tab at once (Session AQW-follow
                    review): two buttons named identically would be ambiguous to a screen reader
                    and to a test alike. */}
                <button
                  onClick={() => setShowEditContact(true)}
                  className="rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
                >
                  Edit contact details
                </button>
              </div>
              <dl className="space-y-1 text-sm">
                <div className="flex justify-between gap-3"><dt className="text-ele-body">Contact</dt><dd className="min-w-0 break-words text-right text-ele-strong">{field(customer.billingContact)}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-ele-body">Email</dt><dd className="min-w-0 break-words text-right text-ele-strong">{customer.billingEmail ? <ContactValue value={customer.billingEmail} kind="email" /> : "Not on record"}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-ele-body">Phone</dt><dd className="min-w-0 break-words text-right text-ele-strong">{customer.phone ? <ContactValue value={customer.phone} kind="phone" /> : "Not on record"}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-ele-body">Sites</dt><dd className="min-w-0 break-words text-right text-ele-strong">{customer._count?.sites ?? 0}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-ele-body">Latest NPS</dt><dd className="min-w-0 break-words text-right text-ele-strong">{customer.latestNpsScore != null ? `${customer.latestNpsScore} / 10` : "Not on record"}</dd></div>
              </dl>

              {/* Manual NPS entry -- for a customer who gives a score over the phone. */}
              <div className="mt-3 border-t border-ele-purple-accent pt-3">
                {npsOpen ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="font-menu text-xs tracking-wide text-ele-body" htmlFor="nps-score">Score 0 to 10</label>
                    <input
                      id="nps-score"
                      type="number"
                      min={0}
                      max={10}
                      value={npsInput}
                      onChange={(e) => setNpsInput(e.target.value)}
                      className="w-20 rounded-ele border border-ele-purple-accent bg-ele-field-inset px-2 py-1 text-sm text-ele-strong outline-none focus:border-ele-green"
                    />
                    <Button variant="secondary" size="sm" onClick={recordNps} disabled={npsSaving}>
                      {npsSaving ? "Saving..." : "Save"}
                    </Button>
                    <button onClick={() => { setNpsOpen(false); setNpsError(null); }} disabled={npsSaving} className="rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50">
                      Cancel
                    </button>
                    {npsError && <p role="alert" className="w-full text-sm text-ele-danger">{npsError}</p>}
                  </div>
                ) : (
                  <button onClick={() => setNpsOpen(true)} className="font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-green">
                    Record NPS score
                  </button>
                )}
              </div>

              {customer.notes && customer.notes.trim() && (
                <p className="mt-3 border-t border-ele-purple-accent pt-3 text-sm text-ele-body">{customer.notes}</p>
              )}

              {/* Secondary contact, moved in with its own working edit control unchanged
                  (Session AQW-follow, Phase 2 C-7): when an email is set it is copied on every
                  customer email. */}
              <div className="mt-4 border-t border-ele-purple-accent pt-4">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <h3 className="font-heading text-base text-ele-blue">Secondary contact</h3>
                  {!editingSec && (
                    <button
                      onClick={startEditSecondary}
                      className="rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
                    >
                      Edit
                    </button>
                  )}
                </div>
                <p className="mb-3 text-xs text-ele-body">
                  A second point of contact (for example a building director). When an email is set here it is copied on every email we send this customer.
                </p>
                {editingSec ? (
                  <div className="space-y-3">
                    {([
                      ["name", "Name"],
                      ["role", "Role"],
                      ["email", "Email"],
                      ["phone", "Phone"],
                    ] as const).map(([key, label]) => (
                      <label key={key} className="block">
                        <span className="mb-1 block font-menu text-xs tracking-wide text-ele-body">{label}</span>
                        <input
                          type={key === "email" ? "email" : "text"}
                          value={secForm[key]}
                          onChange={(e) => setSecForm((f) => ({ ...f, [key]: e.target.value }))}
                          className="w-full rounded-ele border border-ele-purple-accent bg-ele-field-inset px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green"
                        />
                      </label>
                    ))}
                    {secError && <p role="alert" className="text-sm text-ele-danger">{secError}</p>}
                    <div className="flex gap-2">
                      <Button variant="secondary" size="sm" onClick={saveSecondary} disabled={secSaving}>
                        {secSaving ? "Saving..." : "Save"}
                      </Button>
                      <button
                        onClick={() => setEditingSec(false)}
                        disabled={secSaving}
                        className="rounded-ele border border-ele-purple-accent px-4 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between gap-3"><dt className="text-ele-body">Name</dt><dd className="min-w-0 break-words text-right text-ele-strong">{field(customer.secondaryContactName)}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-ele-body">Role</dt><dd className="min-w-0 break-words text-right text-ele-strong">{field(customer.secondaryContactRole)}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-ele-body">Email</dt><dd className="min-w-0 break-words text-right text-ele-strong">{field(customer.secondaryContactEmail)}</dd></div>
                    <div className="flex justify-between gap-3"><dt className="text-ele-body">Phone</dt><dd className="min-w-0 break-words text-right text-ele-strong">{field(customer.secondaryContactPhone)}</dd></div>
                  </dl>
                )}
              </div>

              {/* Forward click-through to this customer's children (Session ADI). Each list reads
                  ?customerId= and shows only this customer's records (see useCustomerFilter). The
                  Sites and Invoices links were previously bare (jumped to the full list); Quotes,
                  Contracts and Projects are new. The opaque customer id is the only thing in the
                  URL, never the customer name. */}
              <div className="mt-4 flex flex-wrap gap-2 border-t border-ele-purple-accent pt-4">
                {([
                  ["Sites", "/office/sites"],
                  ["Quotes", "/office/quotes"],
                  ["Contracts", "/office/contracts"],
                  ["Invoices", "/office/invoices"],
                  ["Projects", "/office/projects"],
                ] as const).map(([label, base]) => (
                  <Link
                    key={label}
                    to={`${base}?customerId=${encodeURIComponent(customer.id)}`}
                    className="rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
                  >
                    {label}
                  </Link>
                ))}
              </div>
            </section>
          </div>

          {/* Sites and lifts, open work, open defects, contracts, open quotes and invoices --
              the account footprint (Session AQW-follow, Phase 2 C-1/C-2/C-3/C-17), fed by the one
              new read GET /api/customers/:id/overview. Replaces the old flat "Sites (N)" list,
              which showed sites with no lifts nested under them. */}
          {/* Named contacts with roles (Session ASN, Phase 9, D86): the people on this account and what each is
              for, including the managing agent (linked to the agent's own customer record when it is one), and the
              accounts this customer is managing agent for. A record only: no automated email reads it. */}
          <ContactsPanel customerId={customer.id} />

          <CustomerOverviewTab customerId={customer.id} />

          {/* Repair estimates for this customer (Session ARL): every one, whatever it was raised
              from, so an estimate whose defect or call-out has since been deleted is still seen,
              answered and deleted here, and nothing the customer delete below is blocked by is out
              of the office's reach. "Quote this" prices a repair for the customer directly. */}
          <RepairEstimatesPanel customerId={customer.id} />
          </div>
          )}

          <ConfirmDeleteModal
            open={showDelete}
            title={`Delete ${customer.name}?`}
            message="This permanently deletes the customer. It is only allowed when the customer has no sites, contracts, quotes or jobs attached: if any exist, the delete is blocked and lists what to remove first. This does not change anything in Xero."
            confirmLabel="Delete customer"
            busy={deleting}
            error={deleteError}
            onConfirm={confirmDelete}
            onCancel={() => { if (!deleting) { setShowDelete(false); setDeleteError(null); } }}
          />

          <EditCustomerContactModal
            open={showEditContact}
            record={{ billingContact: customer.billingContact, billingEmail: customer.billingEmail, phone: customer.phone }}
            error={contactError}
            onClose={() => { setShowEditContact(false); setContactError(null); }}
            onSave={saveContact}
          />

          {showXeroContact && <XeroContactModal customerId={customer.id} onClose={() => setShowXeroContact(false)} />}
          {showXeroLink && <XeroContactLinkModal customerId={customer.id} customerName={customer.name} onClose={() => setShowXeroLink(false)} />}
          {showPromote && (
            <PromoteCustomerModal
              customer={customer}
              onClose={() => setShowPromote(false)}
              onPromoted={(updated) => {
                setCustomer((prev) => (prev ? { ...prev, ...updated } : prev));
                setShowPromote(false);
                setPromoteNotice(`${updated.name} is now a full customer. Existing call-out reports and invoices stay linked; an ELE number is issued only when a contract is signed.`);
              }}
            />
          )}
          {promoteNotice && (
            <p role="status" className="mt-4 rounded-ele border border-ele-green bg-ele-green/10 px-4 py-3 text-sm text-ele-green">
              {promoteNotice}
            </p>
          )}
        </>
      ) : null}
    </div>
  );
}

// Add another Client Portal login to THIS customer (Session AGG). Email is required, the name is an
// optional label so the office can tell two logins apart ("Owner", "Facilities manager"). It creates
// the login only: no invite is emailed, so granting access and sending someone a sign-in link stay
// separate decisions (the new row's own Send invite does the second one).
function AddPortalLoginModal({
  customerId,
  customerName,
  onClose,
  onCreated,
}: {
  customerId: string;
  customerName: string;
  onClose: () => void;
  onCreated: (created: CreatedPortalUser) => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Session PU1: a customer with no ACTIVE contract is refused until the office confirms it in the branded confirm.
  const [needsConfirm, setNeedsConfirm] = useState(false);

  const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
  const labelCls = "mb-1 block font-menu text-xs tracking-wide text-ele-body";
  const ghost = "rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50";

  async function submit(confirmNoActiveContract = false) {
    const trimmed = email.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      const created = await api.portalAdmin.createUser({
        customerId,
        email: trimmed,
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(confirmNoActiveContract ? { confirmNoActiveContract: true } : {}),
      });
      onCreated(created);
    } catch (err) {
      if (isNoActiveContractRefusal(err)) {
        setNeedsConfirm(true);
        setBusy(false);
        return;
      }
      // The server's 409 already says the email is taken (a portal email is unique platform-wide,
      // because a login is resolved from the address alone), so its message is shown as sent.
      setError(err instanceof ApiError && err.message ? err.message : "Could not add the portal login. Please try again.");
      setBusy(false);
    }
  }

  return (
    <>
      {needsConfirm && (
        <NoActiveContractConfirm
          kind="create"
          customerName={customerName}
          recipient={email.trim()}
          busy={busy}
          onConfirm={() => void submit(true)}
          onCancel={() => setNeedsConfirm(false)}
        />
      )}
    <ModalShell ariaLabel="Add portal login" onClose={() => !busy && onClose()}>
      <h2 className="mb-1 font-heading text-xl text-ele-strong">Add portal login</h2>
      <p className="mb-4 text-sm text-ele-body">
        This gives one more person at this customer their own Client Portal sign-in. No email is sent yet.
      </p>

      <div className="space-y-3">
        <div>
          <label className={labelCls} htmlFor="portal-login-email">Email address</label>
          <input
            id="portal-login-email"
            type="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="portal-login-name">Name or role (optional)</label>
          <input
            id="portal-login-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Facilities manager"
            className={inputCls}
          />
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}

      <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
        <button type="button" onClick={onClose} disabled={busy} className={`${ghost} ${MODAL_ACTION_BTN}`}>
          Cancel
        </button>
        <Button size="sm" onClick={() => void submit()} disabled={busy || !email.trim()} className={MODAL_ACTION_BTN}>
          {busy ? "Adding..." : "Add login"}
        </Button>
      </div>
    </ModalShell>
    </>
  );
}

// This customer's Client Portal logins (Session AFH; add + disable added in Session AGG).
// Self-contained: it fetches on mount with its own loading/error state so it never blocks the page.
//
// A customer may hold SEVERAL logins (PortalUser has no unique constraint on customerId, only a
// global unique on email), which is what a managing agent needs: the owner and the facilities
// manager sign in separately. Session AFH already LISTED them all; AGG adds the two controls that
// were missing here, so the whole job is done on the customer's own record:
//   - "Add portal login" creates another login for THIS customer. It deliberately does NOT email an
//     invite: creating access and sending someone a sign-in link are separate decisions, and the row
//     that appears carries its own Send invite.
//   - Per-row Disable / Enable, the platform's "remove access" control on a live customer (Session AB).
//     Disable revokes a customer's access immediately, so it is a two-step ConfirmButton; Enable
//     restores it and needs no confirm.
//   - Remove portal logins (Session BCC, D-BCB-4): an admin on an ARCHIVED customer removes every login
//     for good, so the customer can then be deleted. Previewed, then the branded two-step confirm.
function PortalAccessCard({ customerId, customerName, archived, isAdmin }: { customerId: string; customerName: string; archived: boolean; isAdmin: boolean }) {
  const [logins, setLogins] = useState<PortalAdminUser[]>([]);
  // Session BCC (D-BCB-4): removing every login of an ARCHIVED customer, admin only, so the customer can be deleted.
  const [removal, setRemoval] = useState<{ plan: RemovePortalUsersPlan; afterMessage: string | null; canDeleteAfter: boolean } | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [invitingId, setInvitingId] = useState<string | null>(null);
  const [inviteNotice, setInviteNotice] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [frozenIds, setFrozenIds] = useState<Record<string, number>>({});
  const [adding, setAdding] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  // Session PU1: an invite the server refused because this customer has no ACTIVE contract, waiting on the office's yes.
  const [pendingInvite, setPendingInvite] = useState<PortalAdminUser | null>(null);

  useEffect(() => {
    let live = true;
    setState("loading");
    api.portalAdmin
      .listUsers(customerId)
      .then((rows) => {
        if (!live) return;
        setLogins(rows);
        setState("ready");
      })
      .catch(() => {
        if (live) setState("error");
      });
    return () => {
      live = false;
    };
  }, [customerId]);

  async function sendInvite(u: PortalAdminUser, confirmNoActiveContract = false) {
    setInvitingId(u.id);
    setInviteNotice(null);
    setInviteError(null);
    try {
      await (confirmNoActiveContract ? api.portalAdmin.sendInvite(u.id, { confirmNoActiveContract: true }) : api.portalAdmin.sendInvite(u.id));
      setInviteNotice(`Invite sent to ${u.email}.`);
      setFrozenIds((f) => ({ ...f, [u.id]: Date.now() }));
      window.setTimeout(() => clearRowFeedback(u.id), 60_000);
    } catch (err) {
      if (isNoActiveContractRefusal(err)) {
        // Session PU1: nothing was sent. The branded confirm names the customer and the address.
        setPendingInvite(u);
      } else if (err instanceof ApiError && err.status === 429) {
        setInviteError("Invite already sent recently. Please wait before sending again.");
      } else {
        setInviteError(err instanceof ApiError && err.message ? err.message : "Could not send the invite. Please try again.");
      }
    } finally {
      setInvitingId(null);
    }
  }

  // Session BCC: ask the server what would be removed (a preview writes nothing), then confirm in the branded two-step.
  async function previewRemoval() {
    setInviteNotice(null);
    setInviteError(null);
    setRemoveError(null);
    try {
      const res = await api.customers.removePortalUsers(customerId, true);
      if ("preview" in res) setRemoval({ plan: res.plan, afterMessage: res.afterRemoval.message, canDeleteAfter: res.afterRemoval.canDelete });
    } catch (err) {
      setInviteError(err instanceof ApiError && err.message ? err.message : "Could not check the portal logins. Please try again.");
    }
  }

  async function confirmRemoval() {
    setRemoving(true);
    setRemoveError(null);
    try {
      // Exactly the logins the preview showed: a different set is refused (409 changed) and nothing is removed.
      const res = await api.customers.removePortalUsers(customerId, false, removal!.plan.logins.map((l) => l.id));
      const n = "removed" in res ? res.count : 0;
      setLogins([]);
      setRemoval(null);
      setInviteNotice(`${n === 1 ? "1 portal login" : `${n} portal logins`} removed.`);
    } catch (err) {
      setRemoveError(err instanceof ApiError && err.message ? err.message : "Could not remove the portal logins. Please try again.");
      // The logins changed since the preview: show the current ones rather than retrying a stale confirm.
      if (err instanceof ApiError && err.status === 409) {
        try {
          const fresh = await api.customers.removePortalUsers(customerId, true);
          if ("preview" in fresh) setRemoval({ plan: fresh.plan, afterMessage: fresh.afterRemoval.message, canDeleteAfter: fresh.afterRemoval.canDelete });
          setLogins(await api.portalAdmin.listUsers(customerId));
        } catch {
          setRemoval(null);
        }
      }
    } finally {
      setRemoving(false);
    }
  }

  // Enable / disable one login. The server keeps the row either way, so this is reversible and the
  // list is patched in place rather than refetched (a refetch would drop any in-flight invite freeze).
  async function setDisabled(u: PortalAdminUser, disabled: boolean) {
    setTogglingId(u.id);
    setInviteNotice(null);
    setInviteError(null);
    try {
      const updated = await api.portalAdmin.setDisabled(u.id, disabled);
      setLogins((rows) => rows.map((r) => (r.id === u.id ? { ...r, disabledAt: updated.disabledAt } : r)));
      setInviteNotice(disabled ? `Portal access disabled for ${u.email}.` : `Portal access restored for ${u.email}.`);
      // A disabled login must not keep a stale "Sent" freeze on a button it no longer shows.
      if (disabled) clearRowFeedback(u.id);
    } catch (err) {
      setInviteError(
        err instanceof ApiError && err.message
          ? err.message
          : `Could not ${disabled ? "disable" : "enable"} that login. Please try again.`,
      );
    } finally {
      setTogglingId(null);
    }
  }

  function clearRowFeedback(id: string) {
    setFrozenIds((f) => {
      const next = { ...f };
      delete next[id];
      return next;
    });
  }

  // A newly created login has never signed in and is not disabled: the create response echoes only
  // the stored subset, so those two are filled in explicitly rather than left undefined.
  function onCreated(created: CreatedPortalUser) {
    setLogins((rows) => [...rows, { ...created, lastLoginAt: null, disabledAt: null }]);
    setInviteError(null);
    setInviteNotice(`Portal login added for ${created.email}. Use Send invite to email their verification code.`);
    setAdding(false);
  }

  function lastSignIn(iso: string | null): string {
    return iso ? `Last sign-in: ${formatDateTime(iso)}` : "Never signed in";
  }

  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-heading text-lg text-ele-blue">Portal access</h2>
        <div className="flex flex-wrap items-center gap-2">
          {state === "ready" && archived && isAdmin && logins.length > 0 && (
            <Button variant="secondary" size="sm" onClick={() => void previewRemoval()}>
              Remove portal logins
            </Button>
          )}
          {state === "ready" && (
            <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
              Add portal login
            </Button>
          )}
        </div>
      </div>
      <p className="mb-3 text-xs text-ele-body">
        Client Portal sign-ins for this customer. Each login signs in with a 6-digit verification code sent to their
        email. A customer can have more than one, for example the owner and the facilities manager. Send an invite
        to email a fresh code.
      </p>

      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" ? (
        <p role="alert" className="text-sm text-ele-danger">Could not load the portal access. Please try again in a moment.</p>
      ) : logins.length === 0 ? (
        <EmptyState compact message="No portal access set up." />
      ) : (
        <ul className="space-y-3">
          {logins.map((u) => (
            <li
              key={u.id}
              className="flex flex-wrap items-center justify-between gap-3 border-t border-ele-purple-accent pt-3 first:border-t-0 first:pt-0"
            >
              <div className="min-w-0">
                <p className="truncate text-sm text-ele-strong">{u.email}</p>
                <p className="text-xs text-ele-body">
                  {u.name?.trim() ? `${u.name}, ` : ""}
                  {lastSignIn(u.lastLoginAt)}
                  {u.disabledAt ? <span className="text-ele-danger">, Disabled</span> : null}
                </p>
              </div>
              <div className="flex flex-none items-center gap-3">
                {!u.disabledAt && (
                  <button
                    type="button"
                    onClick={() => sendInvite(u)}
                    disabled={invitingId === u.id || Boolean(frozenIds[u.id])}
                    title={`Email a one-time verification code to ${u.email}.`}
                    className="font-menu text-xs tracking-wide text-ele-blue transition hover:underline disabled:opacity-50"
                  >
                    {invitingId === u.id ? "Sending..." : frozenIds[u.id] ? "Sent" : u.lastLoginAt ? "Send portal link" : "Send invite"}
                  </button>
                )}
                {u.disabledAt ? (
                  <button
                    type="button"
                    onClick={() => void setDisabled(u, false)}
                    disabled={togglingId === u.id}
                    title={`Restore portal access for ${u.email}.`}
                    className="font-menu text-xs tracking-wide text-ele-green transition hover:underline disabled:opacity-50"
                  >
                    {togglingId === u.id ? "Enabling..." : "Enable"}
                  </button>
                ) : (
                  <ConfirmButton
                    onConfirm={() => void setDisabled(u, true)}
                    label="Disable"
                    busy={togglingId === u.id}
                    busyLabel="Disabling..."
                    title={`Revoke portal access for ${u.email}. It can be restored later.`}
                    className="font-menu text-xs tracking-wide text-ele-body transition hover:underline disabled:opacity-50"
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {adding && <AddPortalLoginModal customerId={customerId} customerName={customerName} onClose={() => setAdding(false)} onCreated={onCreated} />}

      {pendingInvite && (
        <NoActiveContractConfirm
          kind="invite"
          customerName={customerName}
          recipient={pendingInvite.email}
          busy={invitingId === pendingInvite.id}
          onConfirm={() => {
            const u = pendingInvite;
            setPendingInvite(null);
            void sendInvite(u, true);
          }}
          onCancel={() => setPendingInvite(null)}
        />
      )}

      <ConfirmDeleteModal
        open={removal !== null}
        title={removal && removal.plan.count === 1 ? "Remove 1 portal login?" : `Remove ${removal?.plan.count ?? 0} portal logins?`}
        message={
          removal
            ? `This permanently removes every Client Portal login of ${removal.plan.customer.name} (${removal.plan.logins.map((l) => l.maskedEmail).join(", ")}). It cannot be undone; a login can be added again later. Nothing is sent to anyone.${
                removal.plan.callOutsUnlinked > 0
                  ? ` ${removal.plan.callOutsUnlinked === 1 ? "1 call-out" : `${removal.plan.callOutsUnlinked} call-outs`} reported through these logins will keep their record but no longer name the login.`
                  : ""
              }`
            : ""
        }
        warning={removal ? (removal.canDeleteAfter ? "Afterwards nothing else was found that would block deleting this customer." : removal.afterMessage) : null}
        confirmLabel="Remove logins permanently"
        busy={removing}
        error={removeError}
        onConfirm={() => void confirmRemoval()}
        onCancel={() => { if (!removing) { setRemoval(null); setRemoveError(null); } }}
      />

      {inviteNotice && (
        <p role="status" className="mt-3 rounded-ele border border-ele-green bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {inviteNotice}
        </p>
      )}
      {inviteError && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {inviteError}
        </p>
      )}
    </section>
  );
}

// This customer's service records across every site and lift (Session ADF). A light, paginated list
// whose one job is reachability: a visit with a record on file (hasJobSheet) opens straight into its
// ServiceRecordPanel via the shared deep-link (a call-out on the Call-outs page, every other type on
// the Service Records page). A scheduled visit has no record yet, so it lists its status with no link.
function CustomerServiceRecords({ customerId }: { customerId: string }) {
  const [items, setItems] = useState<ServiceHistoryItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Session AGZ: card list below md (decided once at mount -- src/lib/mobileLayout.ts).
  const [mobileCards] = useState(initialMobileLayout);
  // Monotonic request token so a slow earlier page can never overwrite a newer one.
  const reqSeq = useRef(0);

  const loadPage = useCallback(
    async (nextPage: number, replace: boolean) => {
      const myReq = ++reqSeq.current;
      setLoading(true);
      setError(false);
      try {
        const res = await api.customers.serviceHistory(customerId, { page: nextPage, limit: 20 });
        if (myReq !== reqSeq.current) return;
        setItems((cur) => (replace ? res.items : [...cur, ...res.items]));
        setPage(res.page);
        setHasMore(res.hasMore);
        setTotal(res.total);
      } catch {
        if (myReq === reqSeq.current) setError(true);
      } finally {
        if (myReq === reqSeq.current) setLoading(false);
      }
    },
    [customerId],
  );

  useEffect(() => {
    void loadPage(1, true);
  }, [loadPage]);

  if (error) {
    return (
      <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
        Could not load the service records. Please try again in a moment.
      </p>
    );
  }
  if (items.length === 0 && !loading) {
    return (
      <EmptyState message="No service records for this customer yet." />
    );
  }

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <span className="text-xs text-ele-body opacity-70">
          {total} visit{total === 1 ? "" : "s"}
        </span>
      </div>
      {mobileCards ? (
        /* Session AGZ mobile card list: full ELE reference, date, outcome, the locked type
           colour, and the record link as a 44px target. */
        <ul className="space-y-3">
          {items.map((v) => {
            return (
              <li key={v.id} className="rounded-ele border border-ele-purple-accent bg-ele-surface px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <span className="min-w-0 flex-1">
                    <span className="block font-mono text-sm font-semibold text-ele-strong">{v.eleNumber}</span>
                    <span className="block text-xs text-ele-body opacity-70">{v.dateIso ? formatDate(v.dateIso) : "No date"}</span>
                    <span className="mt-0.5 block break-words text-sm text-ele-body">{v.outcome}</span>
                  </span>
                  <JobTypeBadge type={v.type} size="sm" />
                </div>
                {v.hasJobSheet && (
                  <Link
                    to={serviceRecordHref(v.id, v.type)}
                    className="mt-1 inline-flex min-h-11 items-center text-xs text-ele-green hover:underline"
                  >
                    Open service record
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
      <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
        <table className="w-full text-left text-sm">
          <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
            <tr>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Lift</th>
              <th className="px-3 py-2 max-md:hidden">Type</th>
              <th className="px-3 py-2">Outcome</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {items.map((v) => {
              return (
                <tr key={v.id} className="border-t border-ele-purple-accent">
                  <td className="px-3 py-2 align-top">{v.dateIso ? formatDate(v.dateIso) : "No date"}</td>
                  <td className="px-3 py-2 align-top font-heading text-ele-strong">{v.eleNumber}</td>
                  <td className="px-3 py-2 align-top max-md:hidden">
                    <JobTypeBadge type={v.type} size="sm" />
                  </td>
                  <td className="px-3 py-2 align-top text-ele-body">{v.outcome}</td>
                  <td className="px-3 py-2 text-right align-top">
                    {v.hasJobSheet && (
                      <Link to={serviceRecordHref(v.id, v.type)} className="text-xs text-ele-green hover:underline">
                        Open service record
                      </Link>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
      <div className="mt-4 flex justify-center">
        {hasMore && (
          <Button variant="secondary" size="sm" onClick={() => loadPage(page + 1, false)} loading={loading}>
            Load more
          </Button>
        )}
        {loading && items.length === 0 && <Loader shape="inline" />}
      </div>
    </div>
  );
}

// ---- Promote a one-off caller (Session AFM) ---------------------------------
// A stepped confirm: 1) the company name, 2) the contacts, 3) review + confirm. The edits PATCH
// the ordinary customer fields; the confirm calls POST /:id/promote, which changes ONLY the
// classification (customerType CONTRACT, isOneOff cleared). Every existing call-out report,
// invoice and site keeps its links, and NO ELE number is issued (that waits for a signing).
function PromoteCustomerModal({
  customer,
  onClose,
  onPromoted,
}: {
  customer: Customer;
  onClose: () => void;
  onPromoted: (updated: Customer) => void;
}) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [name, setName] = useState(customer.name);
  const [contactName, setContactName] = useState(customer.billingContact ?? "");
  const [contactEmail, setContactEmail] = useState(customer.billingEmail ?? "");
  const [contactPhone, setContactPhone] = useState(customer.phone ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
  const labelCls = "mb-1 block font-menu text-xs tracking-wide text-ele-body";
  const ghost = "rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong disabled:opacity-50";

  async function confirmPromote() {
    setBusy(true);
    setError(null);
    try {
      // Persist any edits first (only fields with a value; clearing is not this flow's job).
      const patch: Record<string, unknown> = {};
      if (name.trim() && name.trim() !== customer.name) patch.name = name.trim();
      if (contactName.trim() !== (customer.billingContact ?? "")) patch.billingContact = contactName.trim() || null;
      if (contactEmail.trim() && contactEmail.trim() !== (customer.billingEmail ?? "")) patch.billingEmail = contactEmail.trim();
      if (contactPhone.trim() !== (customer.phone ?? "")) patch.phone = contactPhone.trim() || null;
      if (Object.keys(patch).length > 0) await customers.update(customer.id, patch);
      const updated = await api.customers.promote(customer.id);
      onPromoted({ ...customer, ...patch, ...(updated as unknown as Partial<Customer>) } as Customer);
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not promote the customer. Please try again.");
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Promote to full customer" onClose={() => !busy && onClose()} width="max-w-lg">
      <h2 className="mb-1 font-heading text-xl text-ele-strong">Promote to full customer</h2>
      <p className="mb-4 text-sm text-ele-body">Step {step} of 3</p>

      {step === 1 && (
        <div className="space-y-3">
          <div>
            <label className={labelCls} htmlFor="promote-name">Company name</label>
            <input id="promote-name" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          </div>
          <p className="text-xs text-ele-body">
            Site addresses live on the Sites page: the site created with this caller is already on the register and can be
            completed there.
          </p>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <div>
            <label className={labelCls} htmlFor="promote-contact">Contact name</label>
            <input id="promote-contact" value={contactName} onChange={(e) => setContactName(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls} htmlFor="promote-email">Contact email</label>
            <input id="promote-email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls} htmlFor="promote-phone">Contact phone</label>
            <input id="promote-phone" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className={inputCls} />
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-2 text-sm text-ele-body">
          <p>
            This will convert <span className="text-ele-strong">{name.trim() || customer.name}</span> to a full customer. All
            existing call-out reports and invoices will remain linked.
          </p>
          <p>They will appear in the main customer list. An ELE number is issued only when a contract is signed.</p>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}

      <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
        <button type="button" onClick={() => (step === 1 ? onClose() : setStep((s) => (s === 3 ? 2 : 1)))} disabled={busy} className={`${ghost} ${MODAL_ACTION_BTN}`}>
          {step === 1 ? "Cancel" : "Back"}
        </button>
        {step < 3 ? (
          <Button size="sm" onClick={() => setStep((s) => (s === 1 ? 2 : 3))} disabled={!name.trim()} className={MODAL_ACTION_BTN}>
            Next
          </Button>
        ) : (
          <Button size="sm" onClick={() => void confirmPromote()} disabled={busy} className={MODAL_ACTION_BTN}>
            {busy ? "Promoting..." : "Confirm"}
          </Button>
        )}
      </div>
    </ModalShell>
  );
}
